import {Link, Navigate, Route, Routes, useLocation} from 'react-router-dom'
import {Start} from './Start'
import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { LocaleContext, LoginContext, OverlayContext, ScrollContext } from '../common/App';
import { BurgerButtonBig, CloseButtonBig, HeaderBig, HeaderSmall, LangSelection, HeaderWithButton, Loading, ErrorComp, RadioCheckButton, SquareButton, DropdownInfo, NavigateSmart, SwitchButton } from '../common/Common';
import './WebsiteApp.css';
import { AboutUs } from './AboutUs';
import { Login } from '../common/Login';
import { Register } from './Register';
import { TwoFactorAuthLogin } from '../common/TwoFactorAuthLogin';
import { ForFamilies } from './ForFamilies';
import { sanitizeLocaleMessages, setLoginStatesFromJsonConsumer } from '../common/util';
import { ForMn } from './ForMn';
import { Diversity } from './Diversity';
import { Contact } from './Contact';
import { HowItWorks } from './HowItWorks';
import { ConsumerMenu } from '../ConsumerMenu';
import { LoadState, Location, LocationStartPage, MediationStatus, UserType } from '../common/enums.mjs';
import { version } from 'dompurify';
import { ForgotPassword } from './ForgotPassword';
import { ResetPassword } from './ResetPassword';
import { TermsOfUse } from './TermsOfUse';
import { PrivacyPolicy } from './PrivacyPolicy';
import { Imprint } from './Imprint';
import { ConsumerContext } from '..';
import { PrivacyPolicyRegister } from './PrivacyPolicyRegister';
import { Faq } from './Faq';
import { PrivacyPolicyContact } from './PrivacyPolicyContact';
import { useNavigateSmart } from '../common/hooks';
import { PrivacyPolicyCookies } from './PrivacyPolicyCookies';
import { BlogOverview } from './BlogOverview';
import { Blog } from './Blog';
import { RegisterFirstDataCollection } from './RegisterFirstDataCollection';
import { Locations } from './Locations';
import { locationStartPagesSettings } from '../common/globals_common.mjs';
import { isLocationStartPageActive } from '../common/util_module.mjs';

export const WebsiteContext = createContext();

export function WebsiteApp(props) {
    const locale = useContext(LocaleContext);
    const login = useContext(LoginContext);
    const overlay = useContext(OverlayContext);
    const scroll = useContext(ScrollContext);
    const consumer = useContext(ConsumerContext);

    const localeRef = useRef();

    // For Menu
    const menuRef = useRef(null);
    const menuBtnRef = useRef(null);
    const [menuVisible, setMenuVisible] = useState(false);

    const [loadState, setLoadState] = useState(LoadState.NONE);

    const emptyRegisterInfo = {
        "userType": UserType.NONE,
        "location": Location.NONE
    };
    const [registerInfo, setRegisterInfo] = useState(emptyRegisterInfo);

    const navigate = useNavigateSmart();
    const location = useLocation();


    // FUNCTIONS:

    function handleMenuClick(e) {
        if(menuVisible) {
            setMenuVisible(false);
        }
        else {
            setMenuVisible(true);
        }
    }

    function handleLogin(json) {
        setLoginStatesFromJsonConsumer(json, consumer);

        if(login.navigateUrlAfterLogin.current) {
            navigate(login.navigateUrlAfterLogin.current);
            login.navigateUrlAfterLogin.current = null;
        }
        else {
            navigate("/dashboard/");
        }
    }


    // EFFECTS:

    useLayoutEffect(() => {
        localeRef.current = locale.locale;
    }, [locale.locale]);

    useLayoutEffect(() => {
        setLoadState(LoadState.LOADING);
        overlay.incrLoadingCounter();

        async function loadLocaleData(localeStr) {
          const messages = await import(`./i18n/${localeStr}.json`);
          locale.setMsgs((curMsgs) => {
            return Object.assign(sanitizeLocaleMessages(messages), curMsgs);
          });
          setLoadState(LoadState.LOAD_SUCCESS);
          overlay.decrLoadingCounter();
        }

        loadLocaleData(localeRef.current);
    }, []);  // Only execute this initially as App does not render this component while loading a new locale

    useLayoutEffect(() => {
        function handleClickAnywhere(e) {
            if (menuRef.current && menuBtnRef.current && !menuRef.current.contains(e.target) && !menuBtnRef.current.contains(e.target)) {
                setMenuVisible(false);
            }
        }

        document.addEventListener("click", handleClickAnywhere);
        return () => document.removeEventListener("click", handleClickAnywhere);
    }, []);

    useLayoutEffect(() => {
        // Clear the register info if we are not on a register page anymore
        if(!location.pathname.startsWith("/register/")) {
            setRegisterInfo(emptyRegisterInfo);
        }
    }, [location.pathname]);


    // RENDER:

    // We do not want to render before the locale properties have been loaded
    if(loadState !== LoadState.LOAD_SUCCESS) {
        return null;
    }

    return(
        <WebsiteContext.Provider value={{registerInfo, setRegisterInfo}}>
            <div id="website_app">
                <div ref={menuRef}>
                    <ConsumerMenu menuVisible={menuVisible} onMenuClick={handleMenuClick} />
                </div>

                <HeaderWithButton onClick={handleMenuClick} menuBtnRef={menuBtnRef} />

                <Routes>
                    <Route exact path="/login/" element={
                        <div className="margin_to_header">
                            <Login onLogin={handleLogin} indexUpdate={{
                                    "canonicalPath": "/login/",
                                    "title": locale.msgs.SITE_TITLE_LOGIN,
                                    "description": locale.msgs.SITE_DESCR_LOGIN
                            }} />
                        </div>
                    }></Route>

                    <Route exact path="/register/" element={<RegisterFirstDataCollection registerInfo={registerInfo} setRegisterInfo={setRegisterInfo}
                            indexUpdate={{
                                "canonicalPath": "/register/",
                                "title": locale.msgs.SITE_TITLE_REGISTER,
                                "description": locale.msgs.SITE_DESCR_REGISTER
                            }}/>}>
                    </Route>

                    <Route exact path="/register/account/" element={<Register onRegister={props.onRegister} registerInfo={registerInfo} setRegisterInfo={setRegisterInfo}
                            indexUpdate={{
                                "noIndexNoFollow": true,
                                "title": locale.msgs.SITE_TITLE_REGISTER,
                                "description": locale.msgs.SITE_DESCR_REGISTER
                            }}
                    />}></Route>

                    <Route exact path="/two_factor_auth/" element={
                        <div className="margin_to_header">
                            <TwoFactorAuthLogin onLogin={handleLogin} indexUpdate={{
                                "noIndexNoFollow": true,
                                "title": locale.msgs.SITE_TITLE_LOGIN,
                                "description": locale.msgs.SITE_DESCR_LOGIN
                            }}/>
                        </div>
                    }>
                    </Route>

                    <Route path="/locations/" element={ <Locations indexUpdate={{
                                "canonicalPath": "/",
                                "title": locale.msgs.SITE_TITLE_LOCATIONS,
                                "description": locale.msgs.SITE_DESCR_LOCATIONS
                            }}
                    />}></Route>

                    <Route path="/" element={ <Start indexUpdate={{
                                "canonicalPath": "/",
                                "title": locale.msgs.SITE_TITLE_INDEX,
                                "description": locale.msgs.SITE_DESCR_INDEX,
                                "orgLd": "/customer/jsonld/local_business_general.jsonld"
                            }}
                            location="general"
                    />}></Route>

                    { isLocationStartPageActive(LocationStartPage.MUNICH) ?
                        <Route path={locationStartPagesSettings[LocationStartPage.MUNICH].path}
                                element={ <Start indexUpdate={{
                                    "canonicalPath": locationStartPagesSettings[LocationStartPage.MUNICH].path,
                                    "title": locale.msgs.START_META_TITLE_MUNICH,
                                    "description": locale.msgs.START_META_DESCR_MUNICH,
                                    "faqLd": [
                                        {
                                            "question": locale.msgs.START_FAQ_1_QUESTION_MUNICH,
                                            "answer": locale.msgs.START_FAQ_1_ANSWER_MUNICH
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_2_QUESTION_MUNICH,
                                            "answer": locale.msgs.START_FAQ_2_ANSWER_MUNICH
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_3_QUESTION_MUNICH,
                                            "answer": locale.msgs.START_FAQ_3_ANSWER_MUNICH
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_4_QUESTION_MUNICH,
                                            "answer": locale.msgs.START_FAQ_4_ANSWER_MUNICH
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_5_QUESTION_MUNICH,
                                            "answer": locale.msgs.START_FAQ_5_ANSWER_MUNICH
                                        }
                                    ],
                                    "orgLd": "/customer/jsonld/local_business_" + LocationStartPage.MUNICH + ".jsonld"
                                }}
                                location={LocationStartPage.MUNICH}
                        />}></Route>
                    :
                        null
                    }

                    { isLocationStartPageActive(LocationStartPage.NUREMBERG) ?
                        <Route path={locationStartPagesSettings[LocationStartPage.NUREMBERG].path}
                                element={ <Start indexUpdate={{
                                    "canonicalPath": locationStartPagesSettings[LocationStartPage.NUREMBERG].path,
                                    "title": locale.msgs.START_META_TITLE_NUREMBERG,
                                    "description": locale.msgs.START_META_DESCR_NUREMBERG,
                                    "faqLd": [
                                        {
                                            "question": locale.msgs.START_FAQ_1_QUESTION_NUREMBERG,
                                            "answer": locale.msgs.START_FAQ_1_ANSWER_NUREMBERG
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_2_QUESTION_NUREMBERG,
                                            "answer": locale.msgs.START_FAQ_2_ANSWER_NUREMBERG
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_3_QUESTION_NUREMBERG,
                                            "answer": locale.msgs.START_FAQ_3_ANSWER_NUREMBERG
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_4_QUESTION_NUREMBERG,
                                            "answer": locale.msgs.START_FAQ_4_ANSWER_NUREMBERG
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_5_QUESTION_NUREMBERG,
                                            "answer": locale.msgs.START_FAQ_5_ANSWER_NUREMBERG
                                        }
                                    ],
                                    "orgLd": "/customer/jsonld/local_business_" + LocationStartPage.NUREMBERG + ".jsonld"
                                }}
                                location={LocationStartPage.NUREMBERG}
                        />}></Route>
                    :
                        null
                    }

                    { isLocationStartPageActive(LocationStartPage.HAMBURG) ?
                        <Route path={locationStartPagesSettings[LocationStartPage.HAMBURG].path}
                                element={ <Start indexUpdate={{
                                    "canonicalPath": locationStartPagesSettings[LocationStartPage.HAMBURG].path,
                                    "title": locale.msgs.START_META_TITLE_HAMBURG,
                                    "description": locale.msgs.START_META_DESCR_HAMBURG,
                                    "faqLd": [
                                        {
                                            "question": locale.msgs.START_FAQ_1_QUESTION_HAMBURG,
                                            "answer": locale.msgs.START_FAQ_1_ANSWER_HAMBURG
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_2_QUESTION_HAMBURG,
                                            "answer": locale.msgs.START_FAQ_2_ANSWER_HAMBURG
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_3_QUESTION_HAMBURG,
                                            "answer": locale.msgs.START_FAQ_3_ANSWER_HAMBURG
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_4_QUESTION_HAMBURG,
                                            "answer": locale.msgs.START_FAQ_4_ANSWER_HAMBURG
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_5_QUESTION_HAMBURG,
                                            "answer": locale.msgs.START_FAQ_5_ANSWER_HAMBURG
                                        }
                                    ],
                                    "orgLd": "/customer/jsonld/local_business_" + LocationStartPage.HAMBURG + ".jsonld"
                                }}
                                location={LocationStartPage.HAMBURG}
                        />}></Route>
                    :
                        null
                    }

                    { isLocationStartPageActive(LocationStartPage.BERLIN) ?
                        <Route path={locationStartPagesSettings[LocationStartPage.BERLIN].path}
                                element={ <Start indexUpdate={{
                                    "canonicalPath": locationStartPagesSettings[LocationStartPage.BERLIN].path,
                                    "title": locale.msgs.START_META_TITLE_BERLIN,
                                    "description": locale.msgs.START_META_DESCR_BERLIN,
                                    "faqLd": [
                                        {
                                            "question": locale.msgs.START_FAQ_1_QUESTION_BERLIN,
                                            "answer": locale.msgs.START_FAQ_1_ANSWER_BERLIN
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_2_QUESTION_BERLIN,
                                            "answer": locale.msgs.START_FAQ_2_ANSWER_BERLIN
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_3_QUESTION_BERLIN,
                                            "answer": locale.msgs.START_FAQ_3_ANSWER_BERLIN
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_4_QUESTION_BERLIN,
                                            "answer": locale.msgs.START_FAQ_4_ANSWER_BERLIN
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_5_QUESTION_BERLIN,
                                            "answer": locale.msgs.START_FAQ_5_ANSWER_BERLIN
                                        }
                                    ],
                                    "orgLd": "/customer/jsonld/local_business_" + LocationStartPage.BERLIN + ".jsonld"
                                }}
                                location={LocationStartPage.BERLIN}
                        />}></Route>
                    :
                        null
                    }

                    { isLocationStartPageActive(LocationStartPage.FRANKFURT) ?
                        <Route path={locationStartPagesSettings[LocationStartPage.FRANKFURT].path}
                                element={ <Start indexUpdate={{
                                    "canonicalPath": locationStartPagesSettings[LocationStartPage.FRANKFURT].path,
                                    "title": locale.msgs.START_META_TITLE_FRANKFURT,
                                    "description": locale.msgs.START_META_DESCR_FRANKFURT,
                                    "faqLd": [
                                        {
                                            "question": locale.msgs.START_FAQ_1_QUESTION_FRANKFURT,
                                            "answer": locale.msgs.START_FAQ_1_ANSWER_FRANKFURT
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_2_QUESTION_FRANKFURT,
                                            "answer": locale.msgs.START_FAQ_2_ANSWER_FRANKFURT
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_3_QUESTION_FRANKFURT,
                                            "answer": locale.msgs.START_FAQ_3_ANSWER_FRANKFURT
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_4_QUESTION_FRANKFURT,
                                            "answer": locale.msgs.START_FAQ_4_ANSWER_FRANKFURT
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_5_QUESTION_FRANKFURT,
                                            "answer": locale.msgs.START_FAQ_5_ANSWER_FRANKFURT
                                        }
                                    ],
                                    "orgLd": "/customer/jsonld/local_business_" + LocationStartPage.FRANKFURT + ".jsonld"
                                }}
                                location={LocationStartPage.FRANKFURT}
                        />}></Route>
                    :
                        null
                    }

                    { isLocationStartPageActive(LocationStartPage.COLOGNE) ?
                        <Route path={locationStartPagesSettings[LocationStartPage.COLOGNE].path}
                                element={ <Start indexUpdate={{
                                    "canonicalPath": locationStartPagesSettings[LocationStartPage.COLOGNE].path,
                                    "title": locale.msgs.START_META_TITLE_COLOGNE,
                                    "description": locale.msgs.START_META_DESCR_COLOGNE,
                                    "faqLd": [
                                        {
                                            "question": locale.msgs.START_FAQ_1_QUESTION_COLOGNE,
                                            "answer": locale.msgs.START_FAQ_1_ANSWER_COLOGNE
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_2_QUESTION_COLOGNE,
                                            "answer": locale.msgs.START_FAQ_2_ANSWER_COLOGNE
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_3_QUESTION_COLOGNE,
                                            "answer": locale.msgs.START_FAQ_3_ANSWER_COLOGNE
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_4_QUESTION_COLOGNE,
                                            "answer": locale.msgs.START_FAQ_4_ANSWER_COLOGNE
                                        },
                                        {
                                            "question": locale.msgs.START_FAQ_5_QUESTION_COLOGNE,
                                            "answer": locale.msgs.START_FAQ_5_ANSWER_COLOGNE
                                        }
                                    ],
                                    "orgLd": "/customer/jsonld/local_business_" + LocationStartPage.COLOGNE + ".jsonld"
                                }}
                                location={LocationStartPage.COLOGNE}
                        />}></Route>
                    :
                        null
                    } 

                    <Route path="/about_us/" element={ <AboutUs indexUpdate={{
                                "canonicalPath": "/about_us/",
                                "title": locale.msgs.SITE_TITLE_ABOUT_US,
                                "description": locale.msgs.SITE_DESCR_ABOUT_US
                            }}
                    />}></Route>

                    <Route path="/blog/*" element={ <Blog indexUpdate={{
                                "canonicalPath": "/blog/",
                                "title": locale.msgs.SITE_TITLE_BLOG,
                                "description": locale.msgs.SITE_DESCR_BLOG
                            }}
                    />}></Route>

                    <Route path="/families/" element={ <ForFamilies indexUpdate={{
                                "canonicalPath": "/families/",
                                "title": locale.msgs.SITE_TITLE_FAMILIES,
                                "description": locale.msgs.SITE_DESCR_FAMILIES
                            }}
                    />}></Route>

                    <Route path="/m_n/" element={ <ForMn indexUpdate={{
                                "canonicalPath": "/m_n/",
                                "title": locale.msgs.SITE_TITLE_MN,
                                "description": locale.msgs.SITE_DESCR_MN
                            }}
                    />}></Route>

                    <Route path="/diversity/" element={ <Diversity indexUpdate={{
                                "canonicalPath": "/diversity/",
                                "title": locale.msgs.SITE_TITLE_DIVERSITY,
                                "description": locale.msgs.SITE_DESCR_DIVERSITY
                            }}
                    />}></Route>

                    <Route path="/contact" element={ <Contact indexUpdate={{
                                "canonicalPath": "/contact",
                                "title": locale.msgs.SITE_TITLE_CONTACT,
                                "description": locale.msgs.SITE_DESCR_CONTACT
                            }}
                    />}></Route>

                    <Route path="/how_it_works/" element={ <HowItWorks indexUpdate={{
                                "canonicalPath": "/how_it_works/",
                                "title": locale.msgs.SITE_TITLE_HOW_IT_WORKS,
                                "description": locale.msgs.SITE_DESCR_HOW_IT_WORKS
                            }}
                    />}></Route>

                    <Route path="/forgot_password/" element={ <ForgotPassword indexUpdate={{
                                "noIndexNoFollow": true
                            }}
                    />}></Route>

                    <Route path="/reset_password/" element={ <ResetPassword indexUpdate={{
                                "noIndexNoFollow": true
                            }}
                    />}></Route>

                    <Route path="/terms_of_use/" element={ <TermsOfUse indexUpdate={{
                                "canonicalPath": "/terms_of_use/",
                                "title": locale.msgs.SITE_TITLE_TERMS_OF_USE,
                                "description": locale.msgs.SITE_DESCR_TERMS_OF_USE
                            }}
                    />}></Route>

                    <Route path="/privacy_policy/" element={ <PrivacyPolicy indexUpdate={{
                                "canonicalPath": "/privacy_policy/",
                                "title": locale.msgs.SITE_TITLE_PRIVACY_POLICY,
                                "description": locale.msgs.SITE_DESCR_PRIVACY_POLICY
                            }}
                    />}></Route>

                    <Route path="/privacy_policy_register/" element={ <PrivacyPolicyRegister
                            indexUpdate={{
                                "noIndexNoFollow": true,
                                "title": locale.msgs.SITE_TITLE_PRIVACY_POLICY,
                                "description": locale.msgs.SITE_DESCR_PRIVACY_POLICY
                            }}
                    />}></Route>

                    <Route path="/privacy_policy_contact/" element={ <PrivacyPolicyContact indexUpdate={{
                                "noIndexNoFollow": true,
                                "title": locale.msgs.SITE_TITLE_PRIVACY_POLICY,
                                "description": locale.msgs.SITE_DESCR_PRIVACY_POLICY
                            }}
                    />}></Route>

                    <Route path="/privacy_policy_cookies/" element={ <PrivacyPolicyCookies indexUpdate={{
                                "noIndexNoFollow": true,
                                "title": locale.msgs.SITE_TITLE_PRIVACY_POLICY,
                                "description": locale.msgs.SITE_DESCR_PRIVACY_POLICY
                            }}
                    />}></Route>

                    <Route path="/imprint/" element={ <Imprint indexUpdate={{
                                "canonicalPath": "/imprint/",
                                "title": locale.msgs.SITE_TITLE_IMPRINT,
                                "description": locale.msgs.SITE_DESCR_IMPRINT
                            }}
                    />}></Route>

                    <Route path="/faq/" element={ <Faq indexUpdate={{
                                "canonicalPath": "/faq/",
                                "title": locale.msgs.SITE_TITLE_FAQ,
                                "description": locale.msgs.SITE_DESCR_FAQ
                            }}
                    />}></Route>

                    <Route path="*" element={ <NavigateSmart to="/" />}></Route>
                </Routes>

            </div>
        </WebsiteContext.Provider>
    );
}
